import { generateId } from "@/store/form-builder/useFormStore";
import { getFieldType } from "@/components/features/FormBuilder/data/fieldTypes";
import { isContextTrigger } from "@/components/features/FormBuilder/data/contextTriggers";
import type {
  AssistantDraft,
  AssistantFormSpec,
  AssistantLogicRule,
  AssistantSpecQuestion,
} from "@/api/form-builder/formAssistant.types";
import type {
  ConditionalLogic,
  FormPage,
  FormQuestion,
} from "@/components/features/FormBuilder/types/form.types";

const CHOICE_TYPES = new Set(["radio", "checkbox", "dropdown"]);

// Hands out an existing id at most once; a second claim on it, or no
// existing id at all, gets a fresh one, so the result never repeats an id.
function idClaimer() {
  const used = new Set<string>();
  return (existingId: string | undefined, prefix: string): string => {
    const id = existingId && !used.has(existingId) ? existingId : generateId(prefix);
    used.add(id);
    return id;
  };
}

type Claim = ReturnType<typeof idClaimer>;

// The builder keeps "Other" as a real last option when allowOther is on (rules
// trigger on it); the spec only says allowOther, so the option is put back here.
const OTHER_OPTION = "Other";

function choiceOptions(spec: AssistantSpecQuestion): string[] {
  if (!spec.allowOther) return spec.options;
  return [...spec.options.filter((value) => value !== OTHER_OPTION), OTHER_OPTION];
}

function toQuestion(
  spec: AssistantSpecQuestion,
  existing: FormQuestion | undefined,
  claim: { question: Claim; option: Claim; slot: Claim },
): FormQuestion {
  const isChoice = CHOICE_TYPES.has(spec.type);
  const kept = existing?.type === spec.type ? existing : undefined;
  const id = claim.question(kept?.id, "question");
  const reusesKept = kept?.id === id;
  const optionId = (value: string) =>
    claim.option(
      reusesKept ? kept?.options?.find((option) => option.value === value)?.id : undefined,
      "option",
    );
  const slotId = (slots: { id: string; label: string }[] | undefined, label: string) =>
    claim.slot(reusesKept ? slots?.find((slot) => slot.label === label)?.id : undefined, "slot");
  // The spec cannot express these, so a kept question keeps what it has; only a
  // new question takes the spec's values.
  const carried = reusesKept
    ? {
        allowMultiple: kept?.allowMultiple ?? false,
        allowAdditionalPhotos: kept?.allowAdditionalPhotos,
        allowAdditionalVideos: kept?.allowAdditionalVideos,
        fields: kept?.fields,
        groupConfig: kept?.groupConfig,
      }
    : {
        allowMultiple: false,
        allowAdditionalPhotos: spec.allowAdditionalPhotos,
        allowAdditionalVideos: spec.allowAdditionalVideos,
      };
  return {
    id,
    type: spec.type,
    typeName: getFieldType(spec.type)?.label ?? spec.type,
    question: spec.label,
    placeholder: spec.placeholder,
    required: spec.required,
    description: spec.description,
    unit: spec.unit,
    options: isChoice
      ? choiceOptions(spec).map((value) => ({ id: optionId(value), value }))
      : undefined,
    allowOther: isChoice && spec.allowOther,
    hidden: false,
    photoSlots:
      spec.type === "photo-upload"
        ? spec.photoSlots.map((label) => ({ id: slotId(kept?.photoSlots, label), label }))
        : undefined,
    videoSlots:
      spec.type === "video-upload"
        ? spec.videoSlots.map((label) => ({ id: slotId(kept?.videoSlots, label), label }))
        : undefined,
    ...carried,
    conditionalLogic: [],
    conditionalLogicEnabled: false,
  };
}

// A context rule (e.g. provider type) has no trigger question: its values are the
// context values themselves, and it keeps the context trigger id.
function toRule(
  rule: AssistantLogicRule,
  trigger: FormQuestion | null,
  ids: {
    questions: Map<string, string>;
    sections: Map<string, string>;
    pages: Map<string, string>;
  },
): ConditionalLogic {
  const resolve = (map: Map<string, string>) =>
    rule.targetKeys.map((key) => map.get(key)).filter((id): id is string => Boolean(id));
  const triggerValues = trigger?.options
    ? rule.values
        .map((label) => trigger.options?.find((option) => option.value === label)?.id)
        .filter((id): id is string => Boolean(id))
    : rule.values;
  const isPageAction = rule.action.endsWith("_page");
  const isSectionAction = rule.action.endsWith("_section");
  return {
    id: generateId("cl"),
    triggerQuestionId: trigger ? trigger.id : rule.triggerKey,
    condition: rule.condition,
    triggerValues,
    action: rule.action,
    targetQuestionIds: isPageAction || isSectionAction ? [] : resolve(ids.questions),
    targetSectionIds: isSectionAction ? resolve(ids.sections) : [],
    targetPageIds: isPageAction ? resolve(ids.pages) : [],
  };
}

/**
 * The spec has no repeating-group type, so a proposal can never carry one back.
 * Applying it would delete the group and every field in it, so it is refused.
 */
export class DroppedRepeatingGroupsError extends Error {
  constructor(readonly labels: string[]) {
    super(`The proposal would remove repeating groups: ${labels.join(", ")}`);
    this.name = "DroppedRepeatingGroupsError";
  }
}

// Keys the model handed back from the current draft keep their real ids (and
// a kept question keeps its option and media slot ids), so applying a
// proposal does not turn existing questions into new ones.
export function specToPages(spec: AssistantFormSpec, existingPages: FormPage[] = []): FormPage[] {
  const existingPageIds = new Set(existingPages.map((page) => page.id));
  const existingSectionIds = new Set(
    existingPages.flatMap((page) => page.sections.map((section) => section.id)),
  );
  const existingQuestions = new Map(
    existingPages.flatMap((page) =>
      page.sections.flatMap((section) => section.questions.map((q) => [q.id, q] as const)),
    ),
  );
  const claim = {
    page: idClaimer(),
    section: idClaimer(),
    question: idClaimer(),
    option: idClaimer(),
    slot: idClaimer(),
  };
  const ids = {
    questions: new Map<string, string>(),
    sections: new Map<string, string>(),
    pages: new Map<string, string>(),
  };
  const questionsByKey = new Map<string, FormQuestion>();

  const pages: FormPage[] = spec.pages.map((page) => {
    const pageId = claim.page(existingPageIds.has(page.key) ? page.key : undefined, "page");
    ids.pages.set(page.key, pageId);
    return {
      id: pageId,
      name: page.name,
      sections: page.sections.map((section) => {
        const sectionId = claim.section(
          existingSectionIds.has(section.key) ? section.key : undefined,
          "sec",
        );
        ids.sections.set(section.key, sectionId);
        return {
          id: sectionId,
          name: section.name,
          description: section.description,
          questions: section.questions.map((specQuestion) => {
            const question = toQuestion(
              specQuestion,
              existingQuestions.get(specQuestion.key),
              claim,
            );
            ids.questions.set(specQuestion.key, question.id);
            questionsByKey.set(specQuestion.key, question);
            return question;
          }),
        };
      }),
    };
  });

  for (const rule of spec.logic) {
    const isContextRule = isContextTrigger(rule.triggerKey);
    // A context rule is stored on a question it targets (or the form's first question);
    // which question holds it does not change how it evaluates.
    const owner = isContextRule
      ? (rule.targetKeys.map((key) => questionsByKey.get(key)).find(Boolean) ??
        questionsByKey.values().next().value)
      : questionsByKey.get(rule.triggerKey);
    if (!owner) continue;
    const newRule = toRule(rule, isContextRule ? null : owner, ids);
    owner.conditionalLogic = [...(owner.conditionalLogic ?? []), newRule];
    owner.conditionalLogicEnabled = true;
  }

  const keptIds = new Set(ids.questions.values());
  const droppedGroups = [...existingQuestions.values()].filter(
    (q) => q.type === "repeating-group" && !keptIds.has(q.id),
  );
  if (droppedGroups.length > 0) {
    throw new DroppedRepeatingGroupsError(droppedGroups.map((q) => q.question));
  }
  return pages;
}

// What the model sees of the draft, by key. It is not lossless: settings the
// spec cannot express (multiple answers, repeating groups and their fields)
// are not in it, so specToPages carries them over for kept questions and
// refuses a proposal that would drop a repeating group.
export function pagesToDraft(pages: FormPage[]): AssistantDraft {
  const questions = pages.flatMap((page) => page.sections.flatMap((section) => section.questions));
  const optionLabel = new Map(
    questions.flatMap((q) => (q.options ?? []).map((option) => [option.id, option.value] as const)),
  );
  return {
    pages: pages.map((page) => ({
      key: page.id,
      name: page.name,
      sections: page.sections.map((section) => ({
        key: section.id,
        name: section.name,
        description: section.description ?? "",
        questions: section.questions.map((question) => ({
          key: question.id,
          type: question.type,
          label: question.question,
          required: question.required,
          placeholder: question.placeholder ?? "",
          description: question.description ?? "",
          unit: question.unit ?? "",
          // "Other" is described by allowOther, the way the spec expects it.
          options:
            question.options
              ?.map((option) => option.value)
              .filter((value) => !(question.allowOther && value === OTHER_OPTION)) ?? [],
          allowOther: question.allowOther ?? false,
          photoSlots: question.photoSlots?.map((slot) => slot.label) ?? [],
          videoSlots: question.videoSlots?.map((slot) => slot.label) ?? [],
        })),
      })),
    })),
    logic: questions.flatMap((question) =>
      (question.conditionalLogic ?? []).map((rule) => ({
        triggerKey: rule.triggerQuestionId || question.id,
        condition: rule.condition,
        values: rule.triggerValues.map((value) => optionLabel.get(value) ?? value),
        action: rule.action,
        targetKeys: [
          ...rule.targetQuestionIds,
          ...(rule.targetSectionIds ?? []),
          ...(rule.targetPageIds ?? []),
        ],
      })),
    ),
  };
}
